import $ from "../../libs/jquery-3.3.1.min.js";
import {BaseGameCaChauffe} from "../base/BaseGameCaChauffe";
import {AudioPlaylist} from '../base/AudioPlaylist.js';
import {gsap} from "../../libs/gsap/all.js";
import {Particles} from "./Particles.js";
import {repeat} from "../../libs/nunjucks";

const ONE_HOUR = 60 * 60 * 1000;
const REFRESH_APP_DELAY = ONE_HOUR * 8;

// Constructor
export class GES extends BaseGameCaChauffe {

  constructor() {
    super(`data/ges.json`);

    this.impactSelected = null;

    // Object class
    this.playlist = new AudioPlaylist();

    // Animation timeline
    this.currentTimeline = null; // main timeline
    this.sunrayTimeline = null;
    this.gesrayTimeline = null;
    this.sunArrowTimeline = null;
    this.gesArrowTimeline = null;
    this.rayDuration = 4.0; // Duration of ray movement
    this.rayStaggerDelay = 0.10; // Stagger duration
    this.rayFadeDuration = 0.5; // READ-ONLY, Duration of fade in and fade out
    this.timelineDuration = 6; // Duration of timeline animation

    this.sunProgress = 0; // Duration of timeline animation

    // Wait for all
    Promise.all([
      this.loadSettings(),
      this.loadData(),
      this.loadData("data/cachauffe-global.json")
    ]).then(() => {
      // Preload audio files
      return this.preloadAudio();
    }).then(() => {
      // Init nunjucks
      return this.initNunjucks();
    }).then(() => {
      // Replace strings {{ }}
      return this.renderNunjucks();
    }).then(() => {
      // Convert IMG to inline SVG
      return window.convertSVGToInline();
    }).then(() => {
      // Real game init
      return this.initGame();
    }).catch((error) => {
      console.error("error", error);
      $("#main").append("<div id='error'>Error: " + error.message + "</div>")
    });
  }

  // To be overridden in child games
  initGame() {
    this.initScreenSaver();
    this.goToScreen(1);

    // Build particle
    this.particles = new Particles(this.data);

    // Earth infinite animation
    gsap.to("#earth #sphere", {
      rotate: 360,
      duration: 1000,
      ease: 'linear',
      repeat: -1
    });

    // Temperature infinite animation
    gsap.to("#temperature #moving-temperature", {y: -20, duration: 2, ease: 'power1.inOut', yoyo: true, repeat: -1});
    gsap.to("#temperature #temperature-meter", {y: -75, duration: 3, ease: 'power1.inOut', yoyo: true, repeat: -1})

    // Sun pulse infinite animation
    gsap.to("#sun-sphere", {scale: 1.7, duration: 2.70, ease: 'power1.inOut', yoyo: true, repeat: -1});

    // Sun rays infinite animation
    const sunRayDelay = this.rayDuration / 2; // Delay between rays
    const sunRayCount = $("#sun .sun-arrow").length;
    const sunRayDuration = sunRayDelay - ((sunRayCount - 1) * this.rayStaggerDelay);

    // Start button infinite animation
    gsap.fromTo("#start-button .arrow", {x: -5, opacity: 0}, {
      x: 5,
      opacity: 1.0,
      duration: 0.75,
      ease: 'power1.inOut',
      repeat: -1,
      repeatDelay: 0.75
    });
    gsap.to("#start-button .arrow", {
      opacity: 0,
      duration: 0.75,
      ease: 'power1.inOut',
      delay: 0.75,
      repeatDelay: 0.75,
      repeat: -1
    })

    this.killTimeline(this.sunrayTimeline);
    this.sunrayTimeline = gsap.timeline({repeat: -1});
    this.sunrayTimeline.from("#sun .sun-arrow", {
      width: 0,
      opacity: 0.0,
      duration: sunRayDuration - this.rayFadeDuration - (this.rayStaggerDelay * (sunRayCount - 1)), // remove opacity 0.0 duration
      stagger: this.rayStaggerDelay
    }).to("#sun .sun-arrow", {
      opacity: 0,
      duration: this.rayFadeDuration,
      stagger: this.rayStaggerDelay
    });

    // Sun arrows infinite animation
    this.killTimeline(this.sunArrowTimeline);
    this.sunArrowTimeline = gsap.timeline();

    // sun ray (out)
    [1, 2, 3, 4].forEach((el, index) => {
      const singleRayTl = gsap.timeline({repeat: -1});

      singleRayTl.to(`#sun-rays .sun-ray${el}`, {opacity: 1.0, ease: 'linear', duration: this.rayFadeDuration});
      singleRayTl.to(`#sun-rays .sun-ray${el}`, {
        x: "318px",
        y: "319px",
        ease: 'linear',
        duration: this.rayDuration
      }, '<=');
      singleRayTl.to(`#sun-rays .sun-ray${el}`, {
        opacity: 0.0,
        ease: 'linear',
        duration: this.rayFadeDuration
      }, `-=${this.rayFadeDuration}`);
      this.sunArrowTimeline.add(singleRayTl, index * sunRayDelay);
    });

    // sun ray (in)
    [1, 2, 3, 4].forEach((el, index) => {
      const singleRayTl = gsap.timeline({repeat: -1});
      singleRayTl.to(`#sun-rays-in .sun-ray${el}`, {
        x: "318px",
        y: "319px",
        ease: 'linear',
        duration: this.rayDuration
      }, '<=');
      singleRayTl.to(`#sun-rays-in .sun-ray${el}`, {
        opacity: 0.0,
        ease: 'linear',
        duration: this.rayFadeDuration
      }, `-=${this.rayFadeDuration}`);
      this.sunArrowTimeline.add(singleRayTl, index * sunRayDelay);
    });

    [5].forEach((el, index) => {
      const singleRayTl = gsap.timeline({repeat: -1});

      singleRayTl.to(`#sun-rays .sun-ray${el}`, {opacity: 1.0, ease: 'linear', duration: this.rayFadeDuration});
      singleRayTl.to(`#sun-rays .sun-ray${el}`, {
        y: "-60px",
        ease: 'linear',
        duration: this.rayDuration / 2
      }, '<=');
      singleRayTl.to(`#sun-rays .sun-ray${el}`, {
        opacity: 0.0,
        ease: 'linear',
        duration: this.rayFadeDuration
      }, `-=${this.rayFadeDuration}`);
      this.sunArrowTimeline.add(singleRayTl, 0.0);
    });

    // Ges rays infinite animation
    const gesRayDelay = this.rayDuration / 2; // Delay between rays
    const gesRayCount = $("#ges-rays .ges-ray").length + 1; // +1 for the ray that goes out
    const gesRayDuration = gesRayDelay - ((gesRayCount - 1) * this.rayStaggerDelay);

    this.killTimeline(this.gesrayTimeline);
    this.gesrayTimeline = gsap.timeline({repeat: -1});
    this.gesrayTimeline.from("#earth .earth-arrow", {
      width: 0,
      opacity: 0.0,
      duration: gesRayDuration - this.rayFadeDuration - (this.rayStaggerDelay * (gesRayCount - 1)), // remove opacity 0.0 duration
      stagger: this.rayStaggerDelay
    }).to("#earth .earth-arrow", {
      opacity: 0,
      duration: this.rayFadeDuration,
      stagger: this.rayStaggerDelay,
    });

    // Ges rays infinite animation
    this.killTimeline(this.gesArrowTimeline);
    this.gesArrowTimeline = gsap.timeline();

    // ges ray (in)
    [1, 2].forEach((el, index) => {
      const singleRayTl = gsap.timeline({repeat: -1});
      singleRayTl.to(`#ges-rays .ges-ray${el}`, {opacity: 1.0, ease: 'linear', duration: this.rayFadeDuration});
      singleRayTl.to(`#ges-rays .ges-ray${el}`, {
        x: "208px",
        y: "-265px",
        ease: 'linear',
        duration: this.rayDuration
      }, '<=');
      this.gesArrowTimeline.add(singleRayTl, index * gesRayDelay);
    });
    // ges ray (out)
    [1, 2].forEach((el, index) => {
      const singleRayTl = gsap.timeline({repeat: -1});
      singleRayTl.to(`#ges-ray-out .ges-ray${el}`, {
        x: "208px",
        y: "-265px",
        ease: 'linear',
        duration: this.rayDuration
      });
      singleRayTl.to(`#ges-ray-out .ges-ray${el}`, {
        opacity: 0.0,
        ease: 'linear',
        duration: this.rayFadeDuration
      }, `-=${this.rayFadeDuration}`);
      this.gesArrowTimeline.add(singleRayTl, index * gesRayDelay);
    });
    [3, 4].forEach((el, index) => {
      const singleRayTl = gsap.timeline({repeat: -1, delay: gesRayDelay});
      singleRayTl.to(`#ges-rays .ges-ray${el}`, {opacity: 1.0, ease: 'linear', duration: this.rayFadeDuration});
      singleRayTl.to(`#ges-rays .ges-ray${el}`, {y: "220px", ease: 'linear', duration: this.rayDuration}, '<=');
      singleRayTl.to(`#ges-rays .ges-ray${el}`, {
        opacity: 0.0,
        ease: 'linear',
        duration: this.rayFadeDuration
      }, `-=${this.rayFadeDuration}`);
      this.gesArrowTimeline.add(singleRayTl, index * gesRayDelay);
    });
  }

  // To be overridden in child games
  initScreen(screenId) {

    // Super constructor
    if (super.initScreen(screenId) === false) {
      return false;
    }

    const $screenElement = $("#screen-" + screenId);
    const screenName = $screenElement.data("name");

    console.log('gotoScreen', screenId, screenName);

    if (screenName === "ges-timeline") {
      this.timelineAnimation();
    }
  }

  idleAnimation() {
    // Build animation timeline
    this.killTimeline(this.currentTimeline);
    this.currentTimeline = gsap.timeline();

    $("#ges-cta #ges-buttons button").css("pointer-events", "none");

    // Stop all particles
    this.currentTimeline.to("#ges-particles .ges-particle", {opacity: 0.0, duration: 1.0, ease: 'sine.out'}, 0.0);
    this.currentTimeline.add(() => {
      for (const particle in this.particles.instances) {
        this.particles.instances[particle].pause();
      }
    }, 1.0);

    // Update ges ray (out) thickness
    [1, 2].forEach((el) => {
      this.currentTimeline.to(`#ges-ray-out .ges-ray${el} .tail`, {
        scaleX: 1,
        scaleY: 1,
        transformOrigin: "50% 101%",
        ease: 'sine.out',
        duration: 1,
      }, 0.0);
      this.currentTimeline.to(`#ges-ray-out .ges-ray${el} .tip`, {
        scale: 1,
        transformOrigin: "50% 0%",
        ease: 'sine.out',
        duration: 1,
      }, 0.0);
    });
    // Update ges ray (in) thickness
    [3, 4].forEach((el) => {
      this.currentTimeline.to(`#ges-rays .ges-ray${el} .tail`, {
        scaleX: 1,
        scaleY: 1,
        transformOrigin: "50% 100%",
        ease: 'sine.out',
        duration: 1,
      }, 0.0);
      this.currentTimeline.to(`#ges-rays .ges-ray${el} .tip`, {
        scale: 1,
        transformOrigin: "50% 0%",
        ease: 'sine.out',
        duration: 1,
      }, 0.0);
    });

    this.currentTimeline.to("#earth", {top: "50%", duration: 1, ease: 'sine.out', force3D: true}, 0.0);
    this.currentTimeline.to("#atmosphere", {top: "50%", duration: 1, ease: 'sine.out', force3D: true}, 0.0);
    this.currentTimeline.to(["#sun-rays", "#sun-rays-in"], {x: 0, y: 0, duration: 1, ease: 'sine.out'}, 0.0);
    this.currentTimeline.to("#sun #sun-sphere", {x: 0, y: 0, duration: 1, ease: 'sine.out'}, 0.0);
    this.currentTimeline.to("#sun .sun-arrow", {x: 0, y: 0, duration: 1, ease: 'sine.out'}, 0.0);

    this.currentTimeline.to("#earth-warming circle", {opacity: 0.125, duration: 1, ease: 'sine.out'}, 0.0);
    this.currentTimeline.to("#atmosphere circle", {
      strokeWidth: 10,
      stroke: "#7abecc",
      roundProps: "strokeWidth",
      duration: 1,
      ease: 'sine.out'
    }, 0.0);
    this.currentTimeline.to("#temperature #moving-temperature", {bottom: "22.3%", duration: 1, ease: 'sine.out'}, 0.0);

    this.currentTimeline.to("#atmosphere-text", {opacity: 0, duration: 1, ease: 'sine.out'}, 0.0);
    this.currentTimeline.to("#warming-text", {opacity: 0, duration: 1, ease: 'sine.out'}, 0.0);

    // Hide previously shown impact content
    this.currentTimeline.to("#ges-content .ges-content.shown", {opacity: 0.0, duration: 1, ease: 'sine.out'}, 0.0);
  }

  timelineAnimation() {
    // Build animation timeline
    this.killTimeline(this.currentTimeline);
    this.currentTimeline = gsap.timeline();

    // Move earth & element down
    this.currentTimeline.to("#earth", {top: "41%", duration: 1, ease: 'power1.inOut', force3D: true}, 0.0);
    this.currentTimeline.to("#atmosphere", {top: "41%", duration: 1, ease: 'power1.inOut', force3D: true}, 0.0);
    this.currentTimeline.to("#sun-rays", {x: -84, y: -67, duration: 1, ease: 'power1.inOut'}, 0.0);
    this.currentTimeline.to("#sun-rays-in", {x: -80, y: -60, duration: 1, ease: 'power1.inOut'}, 0.0);
    this.currentTimeline.to("#sun #sun-sphere", {x: -100, y: -90, duration: 1, ease: 'power1.inOut'}, 0.0);
    this.currentTimeline.to("#sun .sun-arrow", {x: -50, y: -50, duration: 1, ease: 'power1.inOut'}, 0.0);

    // Start 1880 to 2024 animation
    const $timelineYearEl = $("#timeline-year");
    $timelineYearEl.find("#timeline-current").text($timelineYearEl.attr("data-timeline-start"));

    this.currentTimeline.to("#timeline-year #timeline-current", {
      innerHTML: $timelineYearEl.attr("data-timeline-end"),
      duration: this.timelineDuration,
      ease: "power1.out",
      onUpdate: () => {
        // Update year (round)
        $timelineYearEl.find("#timeline-current").text(Math.round($timelineYearEl.find("#timeline-current").text()));
      }
    });

    this.currentTimeline.fromTo("#atmosphere-scaling-arrow",
      {opacity: 0}, {opacity: 1, duration: 0.3, ease: "power1.out"}, '<=');

    this.currentTimeline.fromTo("#atmosphere-scaling-arrow",
      {scale: 0.5}, {scale: 1.0, duration: this.timelineDuration, ease: "power1.out"}, '<=');

    this.currentTimeline.fromTo("#atmosphere-scaling-arrow .inner",
      {scale: 0.8}, {scale: 1.0, duration: 0.5, yoyo: true, repeat: -1, ease: "sine.inOut"}, '<=');

    // Update earth warming
    this.currentTimeline.to("#earth-warming circle",
      {opacity: 0.4, duration: this.timelineDuration, ease: "power1.out"}, '<=');

    // Update ges ray (out) thickness
    [1, 2].forEach((el) => {
      this.currentTimeline.to(`#ges-ray-out .ges-ray${el} .tail`, {
        scaleX: 0.5,
        scaleY: 1.3,
        transformOrigin: "50% 101%",
        ease: 'sine.out',
        duration: this.timelineDuration,
      }, '<=');
      this.currentTimeline.to(`#ges-ray-out .ges-ray${el} .tip`, {
        scale: 0.70,
        transformOrigin: "50% 0%",
        ease: 'sine.out',
        duration: this.timelineDuration,
      }, '<=');
    });

    // Update ges ray (in) thickness
    [3, 4].forEach((el) => {
      this.currentTimeline.to(`#ges-rays .ges-ray${el} .tail`, {
        scaleX: 2.75,
        scaleY: 0.8,
        transformOrigin: "50% 100%",
        ease: 'sine.out',
        duration: this.timelineDuration,
      }, '<=');
      this.currentTimeline.to(`#ges-rays .ges-ray${el} .tip`, {
        scale: 1.8,
        transformOrigin: "50% 0%",
        ease: 'sine.out',
        duration: this.timelineDuration,
      }, '<=');
    });

    // Update atmosphere line thickness
    this.currentTimeline.to("#atmosphere circle", {
      strokeWidth: 40,
      roundProps: "strokeWidth",
      duration: this.timelineDuration,
      ease: "power1.out"
    }, '<=');

    // Pulse atmostphere stroke, bring attention to it
    this.currentTimeline.fromTo("#atmosphere circle", {
      stroke: "#7abecc",
    }, {
      // stroke: "#aaf3ff",
      stroke: "#8AD0DD",
      duration: 0.80,
      ease: "sine.inOut",
      repeat: Math.floor(this.timelineDuration / 0.80),
      yoyo: true
    }, '<=');

    // Update temperature
    this.currentTimeline.to("#temperature #moving-temperature", {
      bottom: "35.25%",
      duration: this.timelineDuration,
      ease: "power1.out"
    }, '<=');

    // Show before year
    this.currentTimeline.to("#timeline-year #timeline-before", {opacity: 1.0, duration: 1.0, ease: 'sine.out'});

    // Hide atmosphere scaling arrow
    this.currentTimeline.to("#atmosphere-scaling-arrow", {opacity: 0, duration: 0.3, ease: "power1.out"}, '<=');

    // Show Atmosphere text
    this.currentTimeline.to("#atmosphere-text", {opacity: 1.0, duration: 1.0, ease: 'sine.out'})

    // Show Warming text
    this.currentTimeline.to("#warming-text", {opacity: 1.0, duration: 1.0, ease: 'sine.out'});

    // Show GES Button and Text
    this.currentTimeline.to("#ges-cta #ges-cta-text", {opacity: 1.0, duration: 1.0, ease: 'sine.out'});

    // Show GES Buttons
    this.currentTimeline.to("#ges-cta #ges-buttons button", {
      y: 0,
      opacity: 1.0,
      duration: 1.0,
      stagger: 0.125,
      ease: 'sine.out',
      onComplete: () => {
        $("#ges-cta #ges-buttons button").css("pointer-events", "auto");
      }
    }, '<=50%');
  }

  changeEarthImpact(impactName) {
    // Avoid doing the same animation
    if (this.impactSelected === impactName) return;

    // Reset variable and timeline
    this.killTimeline(this.currentTimeline);
    this.currentTimeline = gsap.timeline();

    // Disable/Enable impact button
    this.currentTimeline.add(() => {
      $("#ges-buttons button").removeClass("shown").css("pointer-events", "none");
      $(`#ges-buttons #${impactName}`).addClass("shown");
    }, 0.0);

    // Stop current particles
    if (this.impactSelected !== null) {
      this.currentTimeline.to(`#ges-particle-${this.impactSelected}`, {
        opacity: 0.0,
        duration: 0.5,
        ease: 'sine.out'
      }, 0.0);
      this.currentTimeline.add(() => {
        this.particles.instances[this.impactSelected].pause();
      }, 0.5);
    }

    // Play new particles
    this.currentTimeline.to(`#ges-particle-${impactName}`, {opacity: 1.0, duration: 0.5, ease: 'sine.out'}, 0.0);
    this.currentTimeline.add(() => {
      // Play particles
      this.particles.instances[impactName].refresh();
    }, 0.0);

    // First time select an impact
    if (this.impactSelected === null) {
      const elements = ["#timeline-year", "#atmosphere-text", "#timeline-text"];
      this.currentTimeline.to(elements, {opacity: 0.0, duration: 0.5, ease: 'sine.out'}, 0.0);
    } else {
      // Hide previously shown impact content
      this.currentTimeline.to("#ges-content .ges-content.shown", {opacity: 0.0, duration: 0.5, ease: 'sine.out'}, 0.0);
      this.currentTimeline.add(() => {
        $("#ges-content .ges-content.shown").removeClass("shown");
      }, 0.0);
    }

    // Show content related to selected impact
    this.currentTimeline.to(`#ges-content .ges-content[data-impact="${impactName}"]`, {
      opacity: 1.0,
      duration: 0.5,
      ease: 'sine.out'
    }, 0.5);
    this.currentTimeline.add(() => {
      $(`.ges-content[data-impact="${impactName}"]`).addClass("shown");
      // Enable user interaction with buttons
      $("#ges-buttons button").css("pointer-events", "auto");

      this.impactSelected = impactName;
    }, 0.5);
  }

  debugSkipAnimation() {
    this.goToScreen(2, () => {
      this.currentTimeline.seek(10000)
      $("#ges-cta #ges-buttons button").css("pointer-events", "auto");
    });
  }


  /**
   * Reset function, used to go back clean version of the game
   * @param screenId
   * @returns {boolean}
   */
  reset(screenId) {

    // Reset redirect timeout
    if (this.redirectTimeout !== false) {
      clearTimeout(this.redirectTimeout);
      this.redirectTimeout = false;
    }

    this.impactSelected = null;

    // Animate and return to default state
    this.idleAnimation();

    const resetCallback = () => {
      // Things that reset after goToScreen
      gsap.set(["#timeline-year", "#timeline-text"], {opacity: 1.0})
      gsap.set("#timeline-year #timeline-before", {opacity: 0.0});
      gsap.set("#ges-cta #ges-cta-text", {opacity: 0.0});
      gsap.set("#ges-cta #ges-buttons button", {y: 25, opacity: 0.0});
      $("#ges-content .ges-content").removeClass("shown");
      $("#ges-buttons button").removeClass("shown");

      // Full app reload after 8 hours
      if (screenId === 1 && inElectron()) {
        if (Date.now() - this.upSince > REFRESH_APP_DELAY) window.refreshApp();
        else console.log("Will request refresh in " + (REFRESH_APP_DELAY - (Date.now() - this.upSince)) / (60 * 1000) + " minutes.")
      }
    }

    // Super
    if (super.reset(screenId, resetCallback) === false) {
      return false;
    }
  }

}
